import { forwardRef, memo, ReactElement, Ref, useRef } from 'react'
import { isEqual } from 'lodash'
import styled, { css } from 'styled-components'

import { Box, Relative, Scrollbar, TableRef, Token } from '@revolut-internal/ui-kit'
import {
  Table,
  TableData,
  TableLoadingState,
  TableProps,
  TableRowHeight,
} from '@revolut-internal/ui-kit-table'
import { TableInstance } from '@revolut-internal/ui-kit-table/types/types'

import { useTableRowHeight } from 'providers/TableRowHeightProvider'

type LimitedTableProps<D extends TableData> = TableProps<D> & {
  rowsToShow?: number
  withSearch?: boolean
  isNested?: boolean
  hideGradient?: boolean
}

const ROW_HEIGHTS = {
  [TableRowHeight.SMALL]: 28,
  [TableRowHeight.MEDIUM]: 36,
  [TableRowHeight.LARGE]: 40,
} as Record<TableRowHeight, number>

const DEFAULT_ROWS_TO_SHOW = 8
const HEADER_BORDERS_SIZE = 2
const BODY_BORDERS_SIZE = 1
const BORDERS_SIZE = HEADER_BORDERS_SIZE + BODY_BORDERS_SIZE

const backgroundImageStyle = css`
  background-image:
    linear-gradient(
      90deg,
      ${Token.color.actionBackground},
      ${Token.color.actionBackground},
      ${Token.color.actionBackground},
      ${Token.color.actionBackground}
    ),
    linear-gradient(
      90deg,
      ${Token.color.groupedBackground},
      ${Token.color.groupedBackground}
    );
`

const TableContainer = styled(Box)<{ hideGradient: boolean }>`
  // new table doesn't support changing full cell bg anymore, so wee need to do this
  // from the outside
  td {
    & > * {
      ${({ hideGradient }) =>
        hideGradient &&
        css`
          mask-image: unset;
        `}
    }

    &:has([data-cell-selectable='true']) {
      cursor: pointer;
      height: 100%;

      &:hover {
        ${backgroundImageStyle};

        filter: contrast(0.9);
      }

      &:has([data-cell-selected='true']) {
        ${backgroundImageStyle};
      }
    }
  }
`

export const LimitedTableBase = <D extends TableData>(
  {
    rowsToShow = DEFAULT_ROWS_TO_SHOW,
    loadingState = TableLoadingState.READY,
    data,
    hasMore,
    hideGradient = false,
    ...rest
  }: LimitedTableProps<D>,
  ref: Ref<TableInstance<D>>,
) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { activeTableRowHeight } = useTableRowHeight()

  const rowHeight = ROW_HEIGHTS[activeTableRowHeight]

  const tableWithHeaderMaxHeight = rowHeight + rowsToShow * rowHeight + BORDERS_SIZE

  return (
    <Relative>
      <TableContainer
        key={tableWithHeaderMaxHeight}
        ref={containerRef}
        maxHeight={tableWithHeaderMaxHeight}
        overflow="scroll"
        borderRadius={Token.radius.r16}
        aria-label="Limited table"
        hideGradient={hideGradient}
      >
        <Table
          ref={ref}
          data={data}
          rowHeight={activeTableRowHeight}
          loadingState={loadingState}
          stickyHeaderTop={0}
          overscan={rowsToShow}
          autoResetAll={false}
          autoResetExpanded={false}
          autoResetPageIndex={false}
          hasMore={hasMore}
          virtual
          {...rest}
        />
      </TableContainer>

      <Scrollbar scrollRef={containerRef} cornerSpace="s-2" />
    </Relative>
  )
}

const LimitedTableWithRef = forwardRef(LimitedTableBase)
export const LimitedTable = memo(LimitedTableWithRef, isEqual) as <D extends TableData>(
  props: LimitedTableProps<D> & { ref?: Ref<TableRef<D>> },
) => ReactElement
